/**
 * Photo Utilities
 *
 * Handles formatting and processing of photos from various sources:
 * - Google Places API photos  (proxied through backend — API key never leaves the server)
 * - S3 storage URLs
 * - Direct URLs
 */

import { GooglePlacePhoto } from '../Models/trips/trip-activity.model';

// Route photo requests through the backend so the Google Maps API key is
// never embedded in client-side URLs or visible in DevTools Network.
const API_BASE = (
  process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1'
).replace(/\/$/, '');
const PHOTO_PROXY_BASE = `${API_BASE}/places/photo`;
const PLACE_PHOTO_IMAGE_ID_PREFIX = 'gpp_';

function toBase64UrlUtf8(input: string): string {
  const utf8Bytes = new TextEncoder().encode(input);
  let binary = '';
  utf8Bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function toPlacePhotoImageId(photoName: string): string {
  return `${PLACE_PHOTO_IMAGE_ID_PREFIX}${toBase64UrlUtf8(photoName)}`;
}

/**
 * Type guard to check if a photo object is a Google Place Photo
 */
export const isGooglePlacePhoto = (photo: any): photo is GooglePlacePhoto => {
  return (
    photo &&
    typeof photo === 'object' &&
    'name' in photo &&
    typeof photo.name === 'string'
  );
};

/**
 * Format Google Places photos into displayable URLs
 * Supports both old (v1 basic) and new (v1 advanced) Google Places API photo formats
 *
 * @param photos - Array of Google Place Photo objects
 * @param maxWidth - Maximum width for the photo (default: 400)
 * @returns Array of formatted photo URLs
 */
export const formatGooglePlacesPhotos = (
  photos: GooglePlacePhoto[],
  maxWidth: number = 400
): string[] => {
  if (!photos || photos.length === 0) {
    return [];
  }

  return photos
    .map((photo) => {
      if (
        photo.name &&
        photo.name.startsWith('places/') &&
        photo.name.includes('/photos/')
      ) {
        // New Places API v1 — prefer canonical imageId route.
        const imageId = toPlacePhotoImageId(photo.name);
        return (
          `${PHOTO_PROXY_BASE}` +
          `?imageId=${encodeURIComponent(imageId)}` +
          `&maxWidthPx=${maxWidth}&maxHeightPx=${maxWidth}`
        );
      }
      return null;
    })
    .filter((url): url is string => url !== null);
};

/**
 * Format S3 storage photo URLs
 * Currently just returns the URLs as-is, but provides consistency and room for future enhancements
 * (e.g., adding CDN prefix, image optimization parameters, etc.)
 *
 * @param photos - Array of S3 photo URLs
 * @returns Array of formatted photo URLs
 */
export const formatS3Photos = (photos: string[]): string[] => {
  if (!photos || photos.length === 0) {
    return [];
  }

  // For now, just return as-is. In the future, we could:
  // - Add CDN prefix
  // - Add image optimization parameters
  // - Validate URLs
  return photos.filter((url) => url && typeof url === 'string');
};

/**
 * Smart photo formatter that detects the photo source and formats accordingly
 *
 * @param photos - Array of photos (can be strings or Google Place Photo objects)
 * @param maxWidth - Maximum width for Google Places photos (default: 400)
 * @returns Array of formatted photo URLs ready for display
 */
export const formatPhotoUrls = (
  photos: (string | GooglePlacePhoto)[] | undefined,
  maxWidth: number = 400
): string[] => {
  if (!photos || photos.length === 0) {
    return [];
  }

  // Check the first photo to determine the type
  const firstPhoto = photos[0];

  if (isGooglePlacePhoto(firstPhoto)) {
    // All photos are Google Place photos
    return formatGooglePlacesPhotos(photos as GooglePlacePhoto[], maxWidth);
  } else if (typeof firstPhoto === 'string') {
    // All photos are direct URLs (S3 or other)
    return formatS3Photos(photos as string[]);
  }

  // Fallback: empty array
  console.warn('[photo-utils] Unknown photo format:', photos);
  return [];
};

/**
 * Get a single photo URL from various sources
 * Useful for thumbnail/preview scenarios
 *
 * @param photo - Single photo (string URL or Google Place Photo object)
 * @param maxWidth - Maximum width for Google Places photos (default: 400)
 * @returns Formatted photo URL or undefined
 */
export const formatSinglePhotoUrl = (
  photo: string | GooglePlacePhoto | undefined,
  maxWidth: number = 400
): string | undefined => {
  if (!photo) return undefined;

  if (isGooglePlacePhoto(photo)) {
    const formatted = formatGooglePlacesPhotos([photo], maxWidth);
    return formatted[0];
  } else if (typeof photo === 'string') {
    return photo;
  }

  return undefined;
};
